import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  Upload, 
  Cpu, 
  CheckCircle, 
  XCircle, 
  Loader2,
  Zap,
  Car
} from 'lucide-react';
import { ProcessingStatus } from '../types';

interface ProcessingProgressProps {
  status: ProcessingStatus;
  progress: number;
  onCancel?: () => void;
  etaSeconds?: number;
}

export function ProcessingProgress({ 
  status, 
  progress, 
  onCancel,
  etaSeconds 
}: ProcessingProgressProps) {
  const statusConfig = useMemo(() => {
    switch (status) {
      case 'idle':
        return {
          icon: Upload,
          title: 'Ready to Process',
          description: 'Upload a video to begin analysis',
          color: 'text-dark-400',
          bgColor: 'bg-dark-700',
          progressColor: 'from-dark-600 to-dark-500'
        };
      case 'uploading':
        return {
          icon: Upload,
          title: 'Uploading Video',
          description: `Transferring file to server... ${progress}%`,
          color: 'text-blue-400',
          bgColor: 'bg-blue-500/10',
          progressColor: 'from-blue-600 to-blue-400'
        };
      case 'processing':
        return {
          icon: Cpu,
          title: 'Analyzing Video',
          description: `AI detection in progress... ${progress}%`,
          color: 'text-primary-400',
          bgColor: 'bg-primary-500/10',
          progressColor: 'from-primary-600 to-accent-500'
        };
      case 'completed':
        return {
          icon: CheckCircle,
          title: 'Analysis Complete',
          description: 'Vehicle detection and speed calculation finished',
          color: 'text-green-400',
          bgColor: 'bg-green-500/10',
          progressColor: 'from-green-600 to-green-400'
        };
      case 'error':
        return {
          icon: XCircle,
          title: 'Processing Failed',
          description: 'An error occurred during analysis',
          color: 'text-red-400',
          bgColor: 'bg-red-500/10',
          progressColor: 'from-red-600 to-red-400'
        };
      default:
        return {
          icon: Upload,
          title: 'Ready',
          description: '',
          color: 'text-dark-400',
          bgColor: 'bg-dark-700',
          progressColor: 'from-dark-600 to-dark-500'
        };
    }
  }, [status, progress]);

  const isActive = status === 'uploading' || status === 'processing';
  const StatusIcon = statusConfig.icon;

  const formatEta = (seconds: number): string => {
    if (seconds < 60) return `${Math.round(seconds)}s remaining`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m remaining`;
    return `${Math.round(seconds / 3600)}h remaining`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`
        relative overflow-hidden rounded-2xl p-6
        ${statusConfig.bgColor}
        border border-dark-700
      `}
    >
      {/* Background pattern */}
      <div className="absolute inset-0 cyber-grid opacity-30" />

      {/* Active processing animation */}
      {isActive && (
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent"
          animate={{ x: ['-100%', '100%'] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
        />
      )}

      <div className="relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <motion.div
              className={`p-3 rounded-xl ${statusConfig.bgColor}`}
              animate={isActive ? { scale: [1, 1.05, 1] } : {}}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              {isActive ? (
                <Loader2 className={`w-6 h-6 ${statusConfig.color} animate-spin`} />
              ) : (
                <StatusIcon className={`w-6 h-6 ${statusConfig.color}`} />
              )}
            </motion.div>

            <div>
              <h3 className={`text-lg font-semibold ${statusConfig.color}`}>
                {statusConfig.title}
              </h3>
              <p className="text-sm text-dark-400">
                {statusConfig.description}
              </p>
            </div>
          </div>

          {/* Cancel button */}
          {isActive && onCancel && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onCancel}
              className="px-4 py-2 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
            >
              Cancel
            </motion.button>
          )}
        </div>

        {/* Progress bar */}
        <div className="relative h-3 bg-dark-800 rounded-full overflow-hidden">
          <motion.div
            className={`absolute inset-y-0 left-0 bg-gradient-to-r ${statusConfig.progressColor} rounded-full`}
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            {/* Shimmer effect */}
            {isActive && (
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                animate={{ x: ['-100%', '100%'] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
              />
            )}
          </motion.div>
        </div>

        {/* Progress details */}
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-4">
            {/* Progress percentage */}
            <div className="flex items-center gap-2">
              <Zap className={`w-4 h-4 ${statusConfig.color}`} />
              <span className="text-sm font-mono text-dark-300">
                {progress.toFixed(0)}%
              </span>
            </div>

            {/* ETA */}
            {isActive && etaSeconds && etaSeconds > 0 && (
              <span className="text-sm text-dark-500">
                {formatEta(etaSeconds)}
              </span>
            )}
          </div>

          {/* Processing stats animation */}
          {status === 'processing' && (
            <motion.div
              className="flex items-center gap-2 text-sm text-dark-400"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Car className="w-4 h-4" />
              <span>Detecting vehicles...</span>
            </motion.div>
          )}
        </div>

        {/* Stage indicators */}
        {isActive && (
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-dark-700">
            {[
              { label: 'Upload', icon: Upload, active: status === 'uploading' },
              { label: 'Detect', icon: Cpu, active: status === 'processing' && progress < 50 },
              { label: 'Track', icon: Car, active: status === 'processing' && progress >= 50 && progress < 80 },
              { label: 'Calculate', icon: Zap, active: status === 'processing' && progress >= 80 }
            ].map((stage, i) => {
              const StageIcon = stage.icon;
              const isPast = 
                (stage.label === 'Upload' && status === 'processing') ||
                (stage.label === 'Detect' && progress >= 50) ||
                (stage.label === 'Track' && progress >= 80);

              return (
                <div
                  key={i}
                  className={`
                    flex flex-col items-center gap-2
                    ${stage.active ? 'text-primary-400' : isPast ? 'text-green-400' : 'text-dark-500'}
                  `}
                >
                  <motion.div
                    className={`
                      p-2 rounded-lg
                      ${stage.active ? 'bg-primary-500/20' : isPast ? 'bg-green-500/20' : 'bg-dark-700'}
                    `}
                    animate={stage.active ? { scale: [1, 1.1, 1] } : {}}
                    transition={{ duration: 1, repeat: Infinity }}
                  >
                    {isPast ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      <StageIcon className={`w-4 h-4 ${stage.active ? 'animate-pulse' : ''}`} />
                    )}
                  </motion.div>
                  <span className="text-xs font-medium">{stage.label}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default ProcessingProgress;
